import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, geleden } from '../i18n.js';
import { St } from './Status.jsx';
import { ICOON } from './iconen.jsx';
import Wachtwoordveld from './Wachtwoordveld.jsx';

/**
 * De koppeling met Cisco Secure Endpoint via de MSSP-portal (docs/besluiten/secure-endpoint-koppeling.md): per
 * klantorganisatie een API-credential (Client ID en API-key), getest en versleuteld bewaard, aan precies één klant in ZERO.
 */
export default function SecureEndpointKoppeling({ klanten }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['se-organisaties'], queryFn: api.seOrganisaties, refetchInterval: 15_000 });
  const [melding, zetMelding] = useState(null);
  const leeg = { name: '', tenant: '', region: 'eu', clientId: '', apiKey: '' };
  const [v, zetV] = useState(leeg);
  const [bezig, zetBezig] = useState(false);
  const doe = async (fn, goed) => {
    zetMelding(null); zetBezig(true);
    try { const m = await fn(); zetMelding({ goed: true, tekst: typeof goed === 'function' ? goed(m) : goed }); return true; }
    catch (e) { zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) }); return false; }
    finally { zetBezig(false); client.invalidateQueries({ queryKey: ['se-organisaties'] }); }
  };
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const orgs = q.data.data;
  const bezet = new Set(orgs.map((o) => o.tenant.code));
  const zet = (veld) => (e) => { const w = typeof e === 'string' ? e : e.target.value; zetV((x) => ({ ...x, [veld]: w })); };

  return (
    <div className="grid-2">
      <div className="stack">
        <section className="card">
          <header><h2>{t('Organisaties in Secure Endpoint')}</h2><span className="meta">{getal(orgs.length)}</span></header>
          {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}</div>}
          {orgs.length ? (
            <table>
              <thead><tr><th>Secure Endpoint</th><th>{t('Klant in ADTS ZERO')}</th><th>{t('Laatst')}</th><th /></tr></thead>
              <tbody>{orgs.map((o) => (
                <tr key={o.id}>
                  <td>{o.name}<br /><span className="muted mono" style={{ fontSize: 12 }}>{o.region.toUpperCase()} · {o.clientId}</span></td>
                  <td>{o.tenant.name}</td>
                  <td style={{ fontSize: 13 }}>{o.syncError ? <span title={o.syncError}><St soort="critical">{t('fout')}</St> <span className="muted">{o.syncError.slice(0, 80)}</span></span>
                    : o.syncedAt ? <>{geleden(o.syncedAt)}{o.computers !== null && <><br /><span className="muted">{t('{n} computers', { n: getal(o.computers) })}</span></>}</>
                    : <span className="muted">{t('nog niet')}</span>}</td>
                  <td style={{ textAlign: 'right' }}><button type="button" className="icoonknop" title={t('Verwijderen')} aria-label={t('Verwijderen')}
                    onClick={() => doe(() => api.verwijderSeOrganisatie(o.id), t('Verwijderd.'))}>{ICOON.prullenbak}</button></td>
                </tr>
              ))}</tbody>
            </table>
          ) : <p className="empty">{t('Nog geen organisaties gekoppeld.')}</p>}
          {orgs.length > 0 && <div className="actions"><button type="button" className="btn" onClick={() => doe(api.seNu, t('Ophalen gestart; de uitkomst staat zo hierboven.'))}>{t('Nu ophalen')}</button></div>}
          <p className="noot">{t('Elke vijf minuten haalt ADTS ZERO de gebeurtenissen en computers op van de organisaties waarvan de klant de module heeft. Alleen lezen: ADTS ZERO wijzigt niets in Secure Endpoint.')}</p>
        </section>
      </div>
      <div className="stack">
        <section className="card">
          <header><h2>{t('Organisatie toevoegen')}</h2></header>
          <form className="form" onSubmit={async (e) => {
            e.preventDefault();
            const gelukt = await doe(async () => (await api.maakSeOrganisatie({ ...v, clientId: v.clientId.trim(), apiKey: v.apiKey.trim() })).data,
              (d) => t('Gekoppeld; {n} computers in deze organisatie.', { n: getal(d.computers) }));
            if (gelukt) zetV(leeg);
          }}>
            <label className="field"><span>{t('Naam (zoals in de MSSP-portal)')}</span><input className="input" value={v.name} onChange={zet('name')} /></label>
            <div className="two">
              <label className="field"><span>{t('Klant in ADTS ZERO')}</span>
                <select className="input" value={v.tenant} onChange={zet('tenant')}>
                  <option value="">{t('— kies —')}</option>
                  {klanten.filter((x) => x.active && !bezet.has(x.code)).map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                </select></label>
              <label className="field"><span>{t('Regio')}</span>
                <select className="input" value={v.region} onChange={zet('region')}>
                  <option value="eu">{t('Europa')}</option><option value="nam">{t('Noord-Amerika')}</option><option value="apjc">{t('Azië-Pacific')}</option>
                </select></label>
            </div>
            <label className="field"><span>Client ID</span><input className="input mono" value={v.clientId} onChange={zet('clientId')} autoComplete="off" /></label>
            <label className="field"><span>API-key</span><Wachtwoordveld waarde={v.apiKey} zet={zet('apiKey')} autoComplete="new-password" /></label>
            <div className="actions"><button className="btn primary" disabled={bezig || v.name.trim().length < 2 || !v.tenant || !v.clientId.trim() || !v.apiKey.trim()}>
              {bezig ? t('Bezig…') : t('Testen en opslaan')}</button></div>
            <p className="noot">{t('Maak in de MSSP-portal per klantorganisatie een API-credential (alleen lezen) en kopieer de Client ID en API-key. ADTS ZERO test ze eerst en bewaart de key versleuteld; hij komt niet terug in het scherm.')}</p>
          </form>
        </section>
      </div>
    </div>
  );
}
